import React, { useCallback, useEffect, useRef, useState } from 'react'
import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import CircularProgress from '@mui/material/CircularProgress'
import Paper from '@mui/material/Paper'
import Tab from '@mui/material/Tab'
import Tabs from '@mui/material/Tabs'
import Tooltip from '@mui/material/Tooltip'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import Typography from '@mui/material/Typography'
import { hashHistory } from 'react-router'
import {
  isAwaitingFirstPremium,
  lateLabel,
  attemptStatusMeta,
  errorMessage,
  formatMoney
} from '../../lib/collections.es6'

export const NAV_TABS = [
  {
    key: 'dashboard',
    label: 'Dashboard',
    path: '/admin/collections/dashboard'
  },
  {
    key: 'upcoming',
    label: 'Upcoming debits',
    path: '/admin/collections/upcoming'
  },
  { key: 'my-queue', label: 'My queue', path: '/admin/collections/my-queue' },
  { key: 'team', label: 'Team', path: '/admin/collections/team' },
  { key: 'at-risk', label: 'At risk', path: '/admin/collections/at-risk' },
  { key: 'attempts', label: 'Attempts', path: '/admin/collections/attempts' },
  { key: 'segments', label: 'Segments', path: '/admin/collections/segments' },
  { key: 'holds', label: 'Holds', path: '/admin/collections/holds' },
  { key: 'runs', label: 'Manual runs', path: '/admin/collections/runs' }
]

/** Tabs across the top of every Collections screen. */
export function CollectionsNav({ active }) {
  return (
    <Tabs
      value={active}
      onChange={(event, key) => {
        const tab = NAV_TABS.filter(t => t.key === key)[0]
        if (tab) hashHistory.push(tab.path)
      }}
      variant="scrollable"
      scrollButtons="auto"
      sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}
    >
      {NAV_TABS.map(t => (
        <Tab key={t.key} value={t.key} label={t.label} />
      ))}
    </Tabs>
  )
}

export function PageHeader({ title, subtitle, actions }) {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        gap: 1,
        mb: 2
      }}
    >
      <Box>
        <Typography variant="h5" component="h1">
          {title}
        </Typography>
        {subtitle ? (
          <Typography variant="body2" color="text.secondary">
            {subtitle}
          </Typography>
        ) : null}
      </Box>
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>{actions}</Box>
    </Box>
  )
}

export function StatusChip({ status, size }) {
  const meta = attemptStatusMeta(status)
  return (
    <Chip
      size={size || 'small'}
      label={meta.label}
      color={meta.color}
      variant={meta.color === 'default' ? 'outlined' : 'filled'}
    />
  )
}

/** The "Late by" cell: days late, "On time", or a tag for a policy still waiting for its first premium. */
export function LateCell({ lateness }) {
  if (isAwaitingFirstPremium(lateness)) {
    return (
      <Tooltip title="This policy has never paid. Its first premium has not been collected yet, so it cannot be late.">
        <Chip
          size="small"
          variant="outlined"
          color="info"
          label={lateLabel(lateness)}
        />
      </Tooltip>
    )
  }
  return <>{lateLabel(lateness)}</>
}

export function KpiCard({ label, value, hint, tone, onClick, help, delta }) {
  const color =
    tone === 'good'
      ? 'success.main'
      : tone === 'bad'
      ? 'error.main'
      : tone === 'warn'
      ? 'warning.main'
      : 'text.primary'
  const deltaColor =
    delta && delta.tone === 'good'
      ? 'success.main'
      : delta && delta.tone === 'bad'
      ? 'error.main'
      : 'text.secondary'
  return (
    <Paper
      variant="outlined"
      onClick={onClick}
      role={onClick ? 'link' : undefined}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={
        onClick
          ? e => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault()
                onClick()
              }
            }
          : undefined
      }
      sx={{
        p: 2,
        height: '100%',
        cursor: onClick ? 'pointer' : 'default',
        '&:hover': onClick ? { borderColor: 'primary.main' } : {},
        '&:focus-visible': {
          outline: '2px solid',
          outlineColor: 'primary.main'
        }
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <Typography variant="overline" color="text.secondary">
          {label}
        </Typography>
        {help ? (
          <Tooltip title={help} arrow>
            <InfoOutlinedIcon
              fontSize="inherit"
              aria-label={`What is ${label}?`}
              sx={{ color: 'text.disabled', fontSize: 16 }}
              onClick={e => e.stopPropagation()}
            />
          </Tooltip>
        ) : null}
      </Box>
      <Typography variant="h5" sx={{ color, fontWeight: 600 }}>
        {value}
      </Typography>
      {delta ? (
        <Typography
          variant="caption"
          sx={{ color: deltaColor, display: 'block' }}
        >
          {delta.text}
        </Typography>
      ) : null}
      {hint ? (
        <Typography variant="caption" color="text.secondary">
          {hint}
        </Typography>
      ) : null}
    </Paper>
  )
}

export function Loading() {
  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
      <CircularProgress size={28} />
    </Box>
  )
}

export function ErrorState({ error, onRetry }) {
  return (
    <Alert
      severity="error"
      action={
        onRetry ? (
          <Button color="inherit" size="small" onClick={onRetry}>
            Retry
          </Button>
        ) : null
      }
      sx={{ mb: 2 }}
    >
      {errorMessage(error)}
    </Alert>
  )
}

export function EmptyState({ children }) {
  return (
    <Paper variant="outlined" sx={{ p: 4, textAlign: 'center' }}>
      <Typography color="text.secondary">{children}</Typography>
    </Paper>
  )
}

/**
 * Loads data and exposes {data, loading, error, reload}. A response that arrives after a newer request
 * was started (or after unmount) is ignored, so quick filter changes can never show stale rows.
 */
export function useAsync(loader, deps) {
  const [state, setState] = useState({ data: null, loading: true, error: null })
  const counter = useRef(0)
  const mounted = useRef(true)

  useEffect(() => {
    mounted.current = true
    return () => {
      mounted.current = false
    }
  }, [])

  const run = useCallback(() => {
    counter.current += 1
    const ticket = counter.current
    setState(prev => ({ data: prev.data, loading: true, error: null }))
    Promise.resolve()
      .then(loader)
      .then(
        response => {
          if (mounted.current && ticket === counter.current) {
            setState({
              data:
                response && response.data !== undefined
                  ? response.data
                  : response,
              loading: false,
              error: null
            })
          }
        },
        error => {
          if (mounted.current && ticket === counter.current) {
            setState({ data: null, loading: false, error })
          }
        }
      )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps)

  useEffect(() => {
    run()
  }, [run])

  return {
    data: state.data,
    loading: state.loading,
    error: state.error,
    reload: run
  }
}

/**
 * Daily collected-vs-failed chart. Plain SVG so it needs no charting dependency: green bars are
 * collections, red bars are failures, each day labelled, with the amount in the tooltip.
 */
export function DailyChart({ series, onDayClick }) {
  if (!series || series.length === 0) {
    return <EmptyState>No attempts in this period yet.</EmptyState>
  }
  const width = 760
  const height = 180
  const pad = { top: 10, right: 10, bottom: 28, left: 30 }
  const max = Math.max.apply(
    null,
    series.map(d => d.succeeded + d.failed).concat([1])
  )
  const slot = (width - pad.left - pad.right) / series.length
  const bar = Math.max(2, Math.min(28, slot * 0.7))
  const y = value =>
    pad.top + (height - pad.top - pad.bottom) * (1 - value / max)
  const every = Math.ceil(series.length / 12)
  return (
    <Box sx={{ overflowX: 'auto' }}>
      <svg
        viewBox={`0 0 ${width} ${height}`}
        width="100%"
        role="img"
        aria-label="Collected and failed attempts per day"
      >
        <line
          x1={pad.left}
          x2={width - pad.right}
          y1={y(0)}
          y2={y(0)}
          stroke="#bbb"
        />
        <text x={4} y={y(max) + 4} fontSize="10" fill="#777">
          {max}
        </text>
        {series.map((d, i) => {
          const x = pad.left + i * slot + (slot - bar) / 2
          const okHeight = y(0) - y(d.succeeded)
          const badHeight = y(0) - y(d.failed)
          return (
            <g
              key={d.day}
              style={{ cursor: onDayClick ? 'pointer' : 'default' }}
              onClick={onDayClick ? () => onDayClick(d.day) : undefined}
            >
              <title>{`${d.day}: ${d.succeeded} collected (${formatMoney(
                d.collected_in_cents
              )}), ${d.failed} failed`}</title>
              <rect
                x={x}
                y={y(d.succeeded + d.failed)}
                width={bar}
                height={badHeight}
                fill="#d32f2f"
              />
              <rect
                x={x}
                y={y(d.succeeded + d.failed) + badHeight}
                width={bar}
                height={okHeight}
                fill="#2e7d32"
              />
              {i % every === 0 ? (
                <text
                  x={x + bar / 2}
                  y={height - 10}
                  fontSize="9"
                  textAnchor="middle"
                  fill="#666"
                >
                  {d.day.slice(5)}
                </text>
              ) : null}
            </g>
          )
        })}
      </svg>
      <Box sx={{ display: 'flex', gap: 2, mt: 0.5 }}>
        <Typography variant="caption">
          <span style={{ color: '#2e7d32' }}>■</span> Collected
        </Typography>
        <Typography variant="caption">
          <span style={{ color: '#d32f2f' }}>■</span> Failed
        </Typography>
      </Box>
    </Box>
  )
}
